import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import * as XLSX from 'xlsx-js-style';
import {
	Alert,
	Box,
	Button,
	Container,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@material-ui/core';
import { DriverServiceUrl, endpoints } from 'utils/factory';
import { toast } from 'sonner';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

const ERROR_DISPLAY_KEYS = ['invoice_id', 'row_ord_no']

const HEADER_TO_ERR_KEY = {
	INVOICEID: 'invoice_id',
	JOB_ID: 'job_id',
	DRIVER_NAME: 'driver_name',
	DATE: 'date',
	TIME: 'TIME',
	DELIVERYCOMMENT: 'DELIVERYCOMMENT',
}

const REQUIRED_FIELDS = [
	'INVOICEID',
	'JOB_ID',
	'DRIVER_NAME',
	'DATE',
	'DELIVERYCOMMENT',
	'TIME',
];
const HEADER_ALIASES = {
	INVOICEID: 'INVOICEID',
	INVOICE_ID: 'INVOICEID',
	JOB_ID: 'JOB_ID',
	JOBID: 'JOB_ID',
	DRIVER_NAME: 'DRIVER_NAME',
	DRIVERNAME: 'DRIVER_NAME',
	DATE: 'DATE',
	TIME: 'TIME',
	DELIVERYCOMMENT: 'DELIVERYCOMMENT',
};

const normalizeHeader = (value) =>
	String(value ?? '')
		.trim()
		.replace(/\s+/g, '')
		.replace(/[^a-zA-Z0-9_]/g, '')
		.toUpperCase();

const normalizeValue = (value) => {
	if (value === undefined || value === null) {
		return '';
	}

	return typeof value === 'string' ? value.trim() : value;
};

const normalizeDate = (value) => {
  if (!value) return "";

  const formats = ["DD-MM-YYYY", "YYYY-MM-DD"];
  const date = moment(String(value).trim(), formats, true);

  return date.isValid() ? date.format("YYYY-MM-DD") : "";
};

const formatDateValue = (value) => {
	if (typeof value === 'number' && Number.isFinite(value)) {
		const formatted = XLSX.SSF?.format('dd-mm-yyyy', value);

		if (formatted && formatted !== 'Invalid date') {
			return formatted;
		}
	}

	if (value instanceof Date) {
		return `${String(value.getDate()).padStart(2, '0')}-${String(value.getMonth() + 1).padStart(2, '0')}-${value.getFullYear()}`;
	}

	return normalizeValue(value);
};

const mapHeader = (header) => HEADER_ALIASES[normalizeHeader(header)] || normalizeHeader(header);

const buildRecords = (rows) => {
	if (!Array.isArray(rows) || rows.length === 0) {
		return [];
	}

	const [rawHeaders, ...dataRows] = rows;
	const headers = rawHeaders.map(mapHeader);

	return dataRows
		.filter((row) => Array.isArray(row) && row.some((cell) => cell !== undefined && cell !== null && String(cell).trim() !== ''))
		.filter((row) => row[0] !== '')
		.map((row) => {
			const record = {};

			headers.forEach((header, index) => {
				record[header] = header === 'DATE' ? formatDateValue(row[index]) : normalizeValue(row[index]);
			});

			return record;
		});
};

const RunnerImport = () => {
	const navigate = useNavigate();
	const [fileName, setFileName] = useState('');
	const [sheetName, setSheetName] = useState('Runner Import');
	const [qtype, setQtype] = useState(1);
	const [records, setRecords] = useState([]);
	const [headers, setHeaders] = useState([]);
	const [previewRows, setPreviewRows] = useState([]);
	const [uploadError, setUploadError] = useState('');
	const [submitMessage, setSubmitMessage] = useState('');
	const [isUploading, setIsUploading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [deliveryDate, setDeliveryDate] = useState('');

	const [errorList, setErrorList] = useState([])
	const errorMap = useMemo(() => new Map(errorList.map((e) => [e.row_ord_no, e])), [errorList])
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	const user = useMemo(
		() => localStorage.getItem('trans-userId') || localStorage.getItem('trans-username') || '',
		[]
	);

	const missingRequiredFields = useMemo(
		() => REQUIRED_FIELDS.filter((field) => {
			if (field === 'DATE' && deliveryDate.trim()) {
				return false;
			}

			return !headers.includes(field);
		}),
		[deliveryDate, headers]
	);

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];

		setUploadError('');
		setSubmitMessage('');

		if (!file) {
			return;
		}

		if (!/\.(xlsx|xls)$/i.test(file.name)) {
			setUploadError('Please upload a valid Excel file (.xlsx or .xls).');
			return;
		}

		setIsUploading(true);

		try {
			const buffer = await file.arrayBuffer();
			const workbook = XLSX.read(buffer, { type: 'array' });
			const firstSheet = workbook.SheetNames[0];

			if (!firstSheet) {
				setUploadError('The workbook does not contain any sheets.');
				return;
			}

			const worksheet = workbook.Sheets[firstSheet];
			const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

			if (!rows.length || rows.length < 2) {
				setUploadError('The sheet must contain a header row and at least one data row.');
				return;
			}

			const parsedRecords = buildRecords(rows);

			if (parsedRecords.length === 0) {
				setUploadError('No data rows were found in the selected sheet.');
				return;
			}

			const extractedHeaders = Object.keys(parsedRecords[0]);

			setFileName(file.name);
			setHeaders(extractedHeaders);
			setRecords(parsedRecords);
			setPreviewRows(parsedRecords.map((row, i) => ({ ...row, row_ord_no: i })));
			setSheetName((current) => current || firstSheet);
		} catch (error) {
			setUploadError('Unable to read the Excel file. Please check the format and try again.');
		} finally {
			setIsUploading(false);
			event.target.value = '';
		}
	};

	const applyFallbackValues = (row, index) => {
		const { TIME, DATE, JOB_ID, DRIVER_NAME, ...rest } = row;

		return {
			...rest,
			job_id: normalizeValue(JOB_ID),
			driver_name: normalizeValue(DRIVER_NAME),
			time_window: normalizeValue(TIME),
			date: normalizeDate(DATE || deliveryDate),
			del_comp_id: normalizeValue(localStorage.getItem("trans-userId")),
			row_ord_no: index,
		};
	};

	const isValidTimeWindow = (value) => {
		if (typeof value !== "string") return false;
		return /^(?:[0-9]|0[0-9]|1\d|2[0-3]):[0-5]\d - (?:[0-9]|0[0-9]|1\d|2[0-3]):[0-5]\d$/.test(value);
	};

	const handleSubmit = async () => {
		setUploadError('');
		setSubmitMessage('');

		const errors = [];

		if (!records.length) {
			errors.push('Upload an Excel file before submitting.');
		}

		if (!user) {
			errors.push('User information is missing from local storage. Please sign in again.');
		}

		if (!sheetName.trim()) {
			errors.push('Sheet name is required.');
		}

		if (missingRequiredFields.length > 0) {
			errors.push(`Missing required columns: ${missingRequiredFields.join(', ')}.`);
		}

		if (errors.length > 0) {
			console.log(errors, "errors")
			setUploadError(errors.join('\n'));
			return;
		}

		const validationErrors = []
		for (let i = 0; i < records.length; i++) {
			const row = records[i];
			const rowErrors = { row_ord_no: i }
			let hasError = false

		const checks = [
			{ key: 'INVOICEID', fn: (v) => !v },
			{ key: 'JOB_ID', fn: (v) => !v },
			{ key: 'DRIVER_NAME', fn: (v) => !v },
			{ key: 'DATE', fn: (v) => !v },
			{ key: 'TIME', fn: (v) => !isValidTimeWindow(v) },
		]

			for (const { key, fn } of checks) {
				if (fn(row[key])) {
					const suffix = HEADER_TO_ERR_KEY[key]
					rowErrors[`err_${suffix}`] = `${key} is required`
					hasError = true
				}
			}

			for (const { key, fn } of checks) {
				if (fn(row[key])) {
					const suffix = HEADER_TO_ERR_KEY[key];

					rowErrors[`err_${suffix}`] =
						key === "TIME"
							? "TIME must be in 24-hour format (HH:mm - HH:mm), e.g. 08:00 - 17:30"
							: `${key} is required`;

					hasError = true;
				}
			}

			if (hasError) validationErrors.push(rowErrors)
		}

		const seen = new Map()
		for (let i = 0; i < records.length; i++) {
			const row = records[i]
			const composite = `${row.INVOICEID}`
			if (!seen.has(composite)) seen.set(composite, [])
			seen.get(composite).push(i)
		}
		let hasDuplicate = false
		for (const indices of seen.values()) {
			if (indices.length > 1) {
				hasDuplicate = true
				for (const idx of indices) {
					let entry = validationErrors.find((e) => e.row_ord_no === idx)
					if (!entry) {
						entry = { row_ord_no: idx }
						validationErrors.push(entry)
					}
					entry.err_invoice_id = 'Duplicate row'
				}
			}
		}

		if (validationErrors.length > 0) {
			toast.warning(hasDuplicate ? 'Contains validation error & duplicate rows' : 'Contains validation Error')
			setErrorList(validationErrors)
			return
		}
		
		const payload = {
			qtype,
			user,
			sheetname: fileName.trim(),
			runsheet: records.map(applyFallbackValues),
		};
        console.log('Submitting payload:', payload);
        
		setIsSubmitting(true);

		try {
			const response = await axios.post(`${DriverServiceUrl}/${endpoints.uploadRunnerSheet}`, payload);
			const data = response?.data;

			if(qtype === 1) {
				let errorData = data?.data?.filter((item)=> !item?.is_valid).map((item) => {
					return Object.keys(item).filter((key) => key.startsWith('err_') || ERROR_DISPLAY_KEYS.includes(key)).reduce((obj, key) => {
						obj[key] = item[key]
						return obj
					}, {})
				})
				setErrorList(errorData)
				console.log(data, errorData, "edd")
			}



			if (data?.success === false) {
				toast.error(data?.errMessage || data?.message || 'Runsheet import failed.');
				return;
			}

			if (qtype === 1) {
				const hasInvalidItems = Array.isArray(data?.data) && data.data.some((item) => item?.is_valid === false);

				if (hasInvalidItems) {
					toast.error('Validation error, please check the table for details.');
					return;
				}
			}
			if (qtype === 2) {
				setPreviewRows([]);
				setRecords([]);
				setHeaders([]);
				navigate('/app/runner-list');
			}

			setSubmitMessage(data?.message || 'Runsheet uploaded successfully.');
		} catch (error) {
			const apiMessage = error?.response?.data?.message;
			setUploadError(apiMessage || 'Unable to submit the runsheet.');
		} finally {
			setIsSubmitting(false);
		}
	};


	console.log(errorList, "Errorsss")
	return (
		<>
			<Helmet>
				<title>Runner Import</title>
			</Helmet>

			<Box
				sx={{
					backgroundColor: 'background.default',
					minHeight: '100%',
					py: 4,
				}}
			>
				<Container maxWidth="xl">
					<Paper
						elevation={2}
						style={{
							borderRadius: 16,
							overflow: 'hidden',
						}}
					>
						<Box
							sx={{
								background: 'linear-gradient(135deg, #000000 0%, #0f0f0f 40%, #1c1c1c 100%)',
								color: '#fff',
								px: 3,
								py: 3,
							}}
						>
							<Typography color="inherit" variant="h4">
								Runner Import
							</Typography>
							<Typography color="inherit" variant="body2" style={{ opacity: 0.85, marginTop: 8 }}>
								Upload a runsheet workbook, preview the extracted rows, and submit the data to the runsheet API.
							</Typography>
						</Box>

						<Box sx={{ p: 3 }}>
							{uploadError ? (
								<Alert severity="error" style={{ marginBottom: 16, whiteSpace: 'pre-line' }}>
									{uploadError}
								</Alert>
							) : null}

							{submitMessage ? (
								<Alert severity="success" style={{ marginBottom: 16 }}>
									{submitMessage}
								</Alert>
							) : null}

							<Grid container spacing={2}>
								{/* <Grid item xs={12} md={4}>
									<TextField
										fullWidth
										label="Sheet Name"
										value={sheetName}
										onChange={(event) => setSheetName(event.target.value)}
										variant="outlined"
									/>
								</Grid> */}

								<Grid item xs={12} md={4}>
									<FormControl variant="outlined" fullWidth>
										<InputLabel id="qtype-label">QType</InputLabel>
										<Select
											labelId="qtype-label"
											value={qtype}
											onChange={(event) => setQtype(Number(event.target.value))}
											label="QType"
										>
											<MenuItem value={1}>Validate Only</MenuItem>
											<MenuItem value={2}>Import</MenuItem>
										</Select>
									</FormControl>
								</Grid>

								<Grid item xs={12} md={4}>
									<TextField
										fullWidth
										label="Selected File"
										value={fileName}
										variant="outlined"
										InputProps={{ readOnly: true }}
									/>
								</Grid>

								<Grid item xs={12} md={4}>
									<TextField
										fullWidth
										label="Delivery Date"
										type="date"
										value={deliveryDate}
										onChange={(event) => setDeliveryDate(event.target.value)}
										variant="outlined"
										InputLabelProps={{ shrink: true }}
										helperText="Used when the Excel sheet does not contain date."
									/>
								</Grid>

								<Grid item xs={12}>
									<Box
										sx={{
											display: 'flex',
											gap: 2,
											flexWrap: 'wrap',
											alignItems: 'center',
											mt: 1,
										}}
									>
										<Button
											component="label"
											color="primary"
											variant="contained"
											disabled={isUploading}
										>
											{isUploading ? 'Reading File...' : 'Upload Excel File'}
											<input
												hidden
												accept=".xls,.xlsx"
												type="file"
												onChange={handleFileChange}
											/>
										</Button>

										<Button
											color="primary"
											variant="outlined"
											disabled={!records.length || isSubmitting}
											onClick={handleSubmit}
										>
											{isSubmitting ? 'Submitting...' : 'Submit Runsheet'}
										</Button>

										<Button
											color="primary"
											variant="outlined"
											sx={{ ml: 'auto' }}
											onClick={() => {
												const link = document.createElement('a');
												link.href = '/runner_import_sample.xlsx';
												link.download = 'runner_import_sample.xlsx';
												document.body.appendChild(link);
												link.click();
												document.body.removeChild(link);
											}}
										>
											Download Sample Excel
										</Button>
									</Box>
								</Grid>

								<Grid item xs={12}>
									<Typography variant="h6" style={{ marginBottom: 8 }}>
										Extracted Columns
									</Typography>
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
										{headers.map((header) => (
											<Box
												key={header}
												sx={{
													backgroundColor: '#eef2ff',
													borderRadius: 999,
													color: '#1e3a8a',
													px: 1.5,
													py: 0.5,
													fontSize: 12,
													fontWeight: 600,
												}}
											>
												{header}
											</Box>
										))}
									</Box>
								</Grid>

								<Grid item xs={12}>
									<Typography variant="h6" style={{ marginBottom: 8 }}>
										Preview
									</Typography>
									<TableContainer style={{ border: '1px solid #e5e7eb', borderRadius: 12 }}>
										<Table size="small">
											<TableHead>
												<TableRow>
													{headers.map((header) => (
														<TableCell key={header} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
															{header}
														</TableCell>
													))}
												</TableRow>
											</TableHead>
											<TableBody>
												{previewRows.length > 0 ? (
													previewRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => (
														<TableRow key={`${rowIndex}-${row.INVOICEID || rowIndex}`}>
															{headers.map((header) => {
																const err = errorMap.get(row.row_ord_no)
																const errSuffix = HEADER_TO_ERR_KEY[header]
																const errKey = `err_${errSuffix}`
																const hasError = errSuffix && err?.[errKey]
																return (
																	<TableCell key={`${header}-${rowIndex}`} style={{ whiteSpace: 'nowrap', background: hasError ? '#ffff5f' : undefined }}>
																		{hasError ? <Tooltip title={err[errKey]} PopperProps={{ style: { zIndex: 9999 } }}><span>{String(row[header] ?? '')}</span></Tooltip> : String(row[header] ?? '')}
																	</TableCell>
																)
															})}
														</TableRow>
													))
												) : (
													<TableRow>
														<TableCell colSpan={headers.length || 1}>
															Upload a file to preview the extracted rows.
														</TableCell>
													</TableRow>
												)}
											</TableBody>
										</Table>
									</TableContainer>
									{previewRows.length > 0 && (
										<TablePagination
											component="div"
											count={previewRows.length}
											page={page}
											onPageChange={(_, newPage) => setPage(newPage)}
											rowsPerPage={rowsPerPage}
											onRowsPerPageChange={(e) => {
												setRowsPerPage(parseInt(e.target.value, 10))
												setPage(0)
											}}
											rowsPerPageOptions={[10, 25, 50, 100]}
										/>
									)}
								</Grid>
								{records?.length > 0 &&
									<Grid item xs={12}>
										<Typography variant="body2" color="textSecondary">
											Required columns: {REQUIRED_FIELDS.join(', ')}
										</Typography>
										{missingRequiredFields.length > 0 ? (
											<Typography variant="body2" style={{ marginTop: 6, color: '#b45309' }}>
												Missing from the uploaded sheet: {missingRequiredFields.join(', ')}.
											</Typography>
										) : null}
										<Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
											Rows loaded: {records.length}
										</Typography>
									</Grid>
								}
							</Grid>
						</Box>
					</Paper>
				</Container>
			</Box>
		</>
	);
};

export default RunnerImport;
